import { readable, type Updater } from "svelte/store";

import { schedule } from "$lib/task-manager/scheduler";
import { clearFileStorage, removeFromFileStorage } from "$lib/storage/opfs";
import { clearCurrentTasks, removeWorkerFromQueue } from "$lib/state/task-manager/current-tasks";

import type { CobaltQueue, CobaltQueueItem, CobaltQueueItemPending, CobaltQueueItemRunning, UUID } from "$lib/types/queue";

const clearPipelineCache = (queueItem: CobaltQueueItem) => {
    if (queueItem.state === "running") {
        for (const [ workerId, item ] of Object.entries(queueItem.pipelineResults)) {
            removeFromFileStorage(item.name);
            delete queueItem.pipelineResults[workerId];
        }
    } else if (queueItem.state === "done") {
        removeFromFileStorage(queueItem.resultFile.name);
    }

    return queueItem;
}

let update: (_: Updater<CobaltQueue>) => void;

export const queue = readable<CobaltQueue>(
    {},
    (_, _update) => { update = _update }
);

export function addItem(item: CobaltQueueItem) {
    update(queueData => {
        queueData[item.id] = item;
        return queueData;
    });

    schedule();
}

export function addPendingItem(item: CobaltQueueItemPending) {
    update(queueData => {
        queueData[item.id] = item;
        return queueData;
    });

    schedule();
}

export function markPendingAsDone(id: UUID) {
    update(queueData => {
        const item = queueData[id];
        if (item && item.state === "pending") {
            queueData[id] = {
                id: item.id,
                state: "done",
                resultFile: new File([], "placeholder"),
                pipeline: [],
                filename: item.filename,
                mediaType: item.mediaType,
            };
        }
        return queueData;
    });

    schedule();
}

export function markPendingAsError(id: UUID, errorCode: string) {
    update(queueData => {
        const item = queueData[id];
        if (item && item.state === "pending") {
            queueData[id] = {
                id: item.id,
                state: "error",
                errorCode,
                pipeline: [],
                canRetry: false,
                filename: item.filename,
                mediaType: item.mediaType,
            };
        }
        return queueData;
    });

    schedule();
}

export function waitForItemCompletion(id: UUID): Promise<{ success: boolean; errorCode?: string }> {
    return new Promise((resolve) => {
        const unsubscribe = queue.subscribe((queueData) => {
            const item = queueData[id];
            if (!item) {
                unsubscribe();
                resolve({ success: false, errorCode: "error.queue.item_not_found" });
            } else if (item.state === "done") {
                unsubscribe();
                resolve({ success: true });
            } else if (item.state === "error") {
                unsubscribe();
                resolve({ success: false, errorCode: item.errorCode });
            }
        });
    });
}

export function itemError(id: UUID, workerId: UUID, error: string) {
    update(queueData => {
        const item = queueData[id];
        if (item && item.state !== "pending") {
            queueData[id] = clearPipelineCache(item);

            queueData[id] = {
                ...item,
                state: "error",
                errorCode: error,
            }
        }
        return queueData;
    });

    removeWorkerFromQueue(workerId);
    schedule();
}

export function itemDone(id: UUID, file: File) {
    update(queueData => {
        const item = queueData[id];
        if (item && item.state !== "pending") {
            queueData[id] = clearPipelineCache(item);

            queueData[id] = {
                ...item,
                state: "done",
                resultFile: file,
            }
        }
        return queueData;
    });

    schedule();
}

export function pipelineTaskDone(id: UUID, workerId: UUID, file: File) {
    update(queueData => {
        const item = queueData[id];

        if (item && item.state === 'running') {
            item.pipelineResults[workerId] = file;
        }

        return queueData;
    });

    removeWorkerFromQueue(workerId);
    schedule();
}

export function itemRunning(id: UUID) {
    update(queueData => {
        const data = queueData[id] as CobaltQueueItemRunning;

        if (data) {
            data.state = 'running';
            data.pipelineResults ??= {};
        }

        return queueData;
    });

    schedule();
}

export function removeItem(id: UUID) {
    update(queueData => {
        const item = queueData[id];
        if (!item) return queueData;

        // pending items don't have a pipeline
        if (item.state !== "pending" && item.pipeline) {
            for (const worker of item.pipeline) {
                removeWorkerFromQueue(worker.workerId);
            }
            clearPipelineCache(item);
        }

        delete queueData[id];
        return queueData;
    });

    schedule();
}

export function clearQueue() {
    update(() => ({}));
    clearCurrentTasks();
    clearFileStorage();
}
